import type {
  IBasePathAnalyticPageWithChild,
  IBasePathAnalyticPageWithParent,
} from '~/modules/path-analysis/types';
import type { IBasePageAnalyticMetric } from '~/modules/page-analytic/types';
import type { IGetTextPrice } from '~/modules/shopify';
import type { TemplateType } from '~/types/template';

import { formatMetricByFormatter, numberWithCommas } from '~/modules/analytics';
import { getPAMetricValueV2 } from '~/modules/page-analytic/modules/analytic';
import { IPAMetricKey } from '~/modules/analytics/types';

import { buildTree, removeDuplicates } from '~/utils/removeDuplicates';
import { PAGE_ANALYTIC_MAPPING } from '~/modules/page-analytic';
import { parseTemplateType } from '~/utils';

export const processPathAnalytics = (
  analyticsData: IBasePathAnalyticPageWithParent[] | undefined,
  locationPath: string,
): IBasePathAnalyticPageWithChild | undefined => {
  if (!analyticsData) return undefined;

  const dataTree = processPathAnalyticsTree(analyticsData);
  return dataTree?.find((item) => item.id === locationPath);
};

export const processPathAnalyticsTree = (
  analyticsData: IBasePathAnalyticPageWithParent[] | undefined,
): IBasePathAnalyticPageWithChild[] | undefined => {
  if (!analyticsData) return undefined;

  const uniqueData = removeDuplicates(analyticsData, ['id', 'parentID', 'level', 'locationPath']);

  const updateData = uniqueData.map((item) => ({
    ...item,
    metric: item?.metric,
  })) as IBasePathAnalyticPageWithParent[];

  const dataTree = buildTree<IBasePathAnalyticPageWithChild>(updateData);

  return dataTree;
};

export const getFunnelChildrenPages = (
  page: IBasePathAnalyticPageWithChild | undefined,
): IBasePathAnalyticPageWithChild[] => {
  if (!page || !page.children) {
    return [];
  }
  return page.children
    .filter((child) => child.id)
    .map((child) => ({
      ...child,
      children: getFunnelChildrenPages(child),
    }));
};

const getTotalSessionsByChild = (page: IBasePathAnalyticPageWithChild): string => {
  const totalSessions =
    page.children?.reduce((acc, child) => acc + (child.metric?.totalSessions || 0), 0) || 0;
  return numberWithCommas(totalSessions.toString());
};

export const getFunnelChartTooltip = (data: {
  page?: IBasePathAnalyticPageWithChild;
  getTextPrice: IGetTextPrice;
}) => {
  const CONTINUATION_SESSIONS_KEY = 'CONTINUATION_SESSIONS_KEY';
  const keys = [
    IPAMetricKey.PAGE_SESSIONS,
    CONTINUATION_SESSIONS_KEY,
    IPAMetricKey.PAGE_VISITORS,
    IPAMetricKey.PAGE_BR,
    IPAMetricKey.PAGE_CTR,
  ];
  const { page, getTextPrice } = data;

  if (!page) return [];
  const metric = page.metric as IBasePageAnalyticMetric;
  const totalContinuationSession = numberWithCommas(
    (page.metric?.totalContinuationSession || 0).toString(),
  );

  const baseInfo = [
    {
      key: page.pageTitle || parseTemplateType(page.pageType as TemplateType),
      value: page.locationPath as string,
    },
  ];

  const metricInfo = keys.map((key) => {
    if (key === CONTINUATION_SESSIONS_KEY) {
      return {
        key: 'Continuation sessions',
        value: totalContinuationSession,
      };
    }

    const metricKey = key as IPAMetricKey;
    const value = getPAMetricValueV2(metric, metricKey);
    return {
      key: PAGE_ANALYTIC_MAPPING[metricKey].heading.title as string,
      value: formatMetricByFormatter({
        value: value.value,
        formatter: value.formatter,
        getTextPrice,
      }),
    };
  });

  return baseInfo.concat(metricInfo);
};
